if (typeof ProductBundle !== 'function') {
  class ProductBundle extends HTMLElement {
    constructor() {
      super();

      this.storeSelector = document.querySelector('store-selector[data-main-selector]');
      this.defaultAddToCartText = this.getAttribute('data-add-to-cart-text');
      this.defaultOutOfStockText = this.getAttribute('data-out-of-stock-text');

      this.handleStoreChanged = this.updateForm.bind(this);

      const event = new Event('product-bundle-recommendations-loaded');

      fetch(this.dataset.url)
        .then((response) => response.text())
        .then((text) => {
          const innerHTML = new DOMParser()
            .parseFromString(text, 'text/html')
            .querySelector('product-bundle');

          if (innerHTML) {
            const productCount = Number(innerHTML.dataset.productCount);
            if (productCount === 0) return;

            this.innerHTML = innerHTML.innerHTML;
            this.dispatchEvent(event);
          }
        })
        .catch((e) => {
          this.hidden = true;
        });
    }

    connectedCallback() {
      const tryInit = () => {
        const quickAdd = this.querySelector('quick-add-to-cart');
        const form = this.querySelector('[data-add-to-cart-form]');

        if (quickAdd && form) {
          this.init();
          return true;
        }
        return false;
      };

      if (tryInit()) return;

      const observer = new MutationObserver(() => {
        if (tryInit()) {
          observer.disconnect();
        }
      });

      observer.observe(this, {
        childList: true,
        subtree: true,
      });
    }

    disconnectedCallback() {
      document.removeEventListener('storechanged', this.handleStoreChanged);
    }

    init() {
      if (this._initialized) return;
      this._initialized = true;

      this.form = this.querySelector('[data-add-to-cart-form]');
      if (!this.form) return;

      this.productForm = this.form.querySelector('product-form');
      this.addToCart = this.productForm?.querySelector('[type="submit"]');
      this.addToCartText = this.addToCart?.querySelector('.button__text');

      if (this.productForm) {
        this.productForm.addEventListener('add-to-cart', this.updateCart.bind(this));
      }

      this.addEventListener('variant-selector:changed', this.updateForm.bind(this));

      document.addEventListener('storechanged', this.handleStoreChanged);

      this.bundlePriceEl = this.querySelector('[data-js-bundle-price-original]');
      this.bundleCompareEl = this.querySelector('[data-js-bundle-price-compare]');

      this.updateForm();
    }

    toCents(value) {
      const n = Number(value);
      return Number.isFinite(n) ? n : 0;
    }

    formatMoney(cents) {
      const moneyFormat =
        window.KROWN?.settings?.money_format ||
        window.theme?.money_format ||
        window.Shopify?.money_format;

      if (
        window.Shopify &&
        typeof window.Shopify.formatMoney === 'function' &&
        moneyFormat
      ) {
        return window.Shopify.formatMoney(cents, moneyFormat);
      }

      return (cents / 100).toLocaleString(undefined, {
        style: 'currency',
        currency: 'CAD',
      });
    }

    updateBundlePrice(products) {
      if (!this.bundlePriceEl || !this.bundleCompareEl) return;

      let totalPrice = 0;
      let totalCompare = 0;

      for (const p of products) {
        const v = p?.currentVariant;
        if (!v) continue;

        const price = this.toCents(v.price);
        const compare = this.toCents(
          v.compare_at_price ?? v.compareAtPrice ?? v.compare_at ?? 0
        );

        totalPrice += price;
        totalCompare += compare > price ? compare : price;
      }

      this.bundlePriceEl.textContent = this.formatMoney(totalPrice);

      if (totalCompare > totalPrice) {
        const saving = totalCompare - totalPrice;

        this.bundleCompareEl.style.display = '';
        this.bundleCompareEl.innerHTML = `
          <span>${this.formatMoney(totalCompare)}</span>
          <span>(${this.formatMoney(saving)} off)</span>
        `;
      } else {
        this.bundleCompareEl.style.display = 'none';
        this.bundleCompareEl.innerHTML = '';
      }
    }

    updateCart() {
      if (!document.body.classList.contains('template-cart')) {
        document.getElementById('site-cart-sidebar')?.show?.();
      }

      document.getElementById('cart-recommendations')?.generateRecommendations?.();

      document.querySelectorAll('[data-header-cart-total]').forEach((elm) => {
        const total = document.querySelector('#AjaxCartForm [data-cart-total]');
        if (total) elm.textContent = total.textContent;
      });

      document.querySelectorAll('[data-header-cart-count]').forEach((elm) => {
        const count = document.querySelector('#AjaxCartForm [data-cart-count]');
        if (count) elm.textContent = count.textContent;
      });
    }

    async updateForm() {
      if (!this.form) return;

      await customElements.whenDefined('quick-add-to-cart');
      if (!this.isConnected || !this.form.isConnected) return;

      const inputIds = this.form.querySelectorAll('[name^="id"]');
      const products = Array.from(
        this.querySelectorAll('product-item quick-add-to-cart')
      );

      if (!products.length || !inputIds.length) return;

      for (const [idx, product] of products.entries()) {
        await product.ensureLocation();
        if (!this.isConnected || !this.form.isConnected) return;
        if (inputIds[idx] && product?.currentVariant?.id) {
          inputIds[idx].value = product.currentVariant.id;
        }
      }

      const hasUnavailableVariant = products.some(
        (product) => !product?.currentVariant?.available
      );

      this.updateAddToCart(hasUnavailableVariant);
      this.updateBundlePrice(products);
    }

    updateAddToCart(disabled) {
      if (!this.addToCart) return;

      if (disabled) {
        this.addToCart.disabled = true;
        if (this.addToCartText) {
          this.addToCartText.textContent =
            this.defaultOutOfStockText ||
            window.KROWN?.settings?.locales?.products_sold_out_variant ||
            'Sold out';
        }
      } else {
        this.addToCart.disabled = false;
        if (this.addToCartText) {
          this.addToCartText.textContent =
            this.defaultAddToCartText ||
            window.KROWN?.settings?.locales?.products_add_to_cart_button ||
            'Add to cart';
        }
      }
    }
  }

  if (typeof customElements.get('product-bundle') === 'undefined') {
    customElements.define('product-bundle', ProductBundle);
  }
}
